/**
 * Room Template Types
 * 
 * Shared TypeScript interfaces for room template system
 * Date: 15-10-2025 16:00:00
 * 
 * @module shared/types/room-template
 */

/**
 * Room template categories
 */
export type RoomTemplateCategory = 
  | 'OPERATING_ROOM'
  | 'ICU'
  | 'EMERGENCY'
  | 'GENERAL'
  | 'PEDIATRIC'
  | 'CARDIOLOGY'
  | 'NEUROLOGY'
  | 'ONCOLOGY'
  | 'ORTHOPEDICS'
  | 'RADIOLOGY';

/**
 * Room template interface
 */
export interface RoomTemplate {
  id: string;
  name: string;
  description?: string;
  category: RoomTemplateCategory;
  defaultWidth: number;
  defaultHeight: number;
  defaultCapacity: number;
  allowedSpecialties: string[];
  color: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

/**
 * Create room template data interface
 */
export interface CreateRoomTemplateData {
  name: string;
  description?: string;
  category: RoomTemplateCategory;
  defaultWidth?: number;
  defaultHeight?: number;
  defaultCapacity?: number;
  allowedSpecialties?: string[];
  color?: string;
  isActive?: boolean;
}

/**
 * Update room template data interface
 */
export interface UpdateRoomTemplateData extends Partial<CreateRoomTemplateData> {}

/**
 * Room template filters interface
 */
export interface RoomTemplateFilters {
  category?: RoomTemplateCategory;
  isActive?: boolean;
  search?: string;
  page?: number;
  limit?: number;
}

/**
 * Room template response interface
 */
export interface RoomTemplateResponse {
  success: boolean;
  data?: {
    templates?: RoomTemplate[];
    template?: RoomTemplate;
    usageCount?: number;
  };
  error?: {
    code: string;
    message: string;
    details?: any;
  };
  message?: string;
}

/**
 * Canvas room creation data interface
 */
export interface CanvasRoomCreationData {
  floorId: string;
  templateId?: string;
  position: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
  properties: {
    name: string;
    number: string;
    capacity: number;
  };
}

/**
 * Room template category display mapping
 */
export const ROOM_TEMPLATE_CATEGORY_LABELS: Record<RoomTemplateCategory, string> = {
  OPERATING_ROOM: 'Operating Room',
  ICU: 'ICU',
  EMERGENCY: 'Emergency Room',
  GENERAL: 'General Ward',
  PEDIATRIC: 'Pediatric',
  CARDIOLOGY: 'Cardiology',
  NEUROLOGY: 'Neurology',
  ONCOLOGY: 'Oncology',
  ORTHOPEDICS: 'Orthopedics',
  RADIOLOGY: 'Radiology'
};

/**
 * Default room template colors by category
 */
export const DEFAULT_TEMPLATE_COLORS: Record<RoomTemplateCategory, string> = {
  OPERATING_ROOM: '#e8f5e8',  // Light green
  ICU: '#e3f2fd',             // Light blue
  EMERGENCY: '#ffebee',       // Light red
  GENERAL: '#f5f5f5',         // Light gray
  PEDIATRIC: '#fff3e0',       // Light orange
  CARDIOLOGY: '#fce4ec',      // Light pink
  NEUROLOGY: '#f3e5f5',       // Light purple
  ONCOLOGY: '#e0f2f1',        // Light teal
  ORTHOPEDICS: '#fff8e1',     // Light yellow
  RADIOLOGY: '#e8eaf6'        // Light indigo
};

/**
 * Default room template sizes by category
 */
export const DEFAULT_TEMPLATE_SIZES: Record<RoomTemplateCategory, { width: number; height: number; capacity: number }> = {
  OPERATING_ROOM: { width: 150, height: 120, capacity: 5 },
  ICU: { width: 120, height: 100, capacity: 2 },
  EMERGENCY: { width: 180, height: 140, capacity: 8 },
  GENERAL: { width: 100, height: 80, capacity: 1 },
  PEDIATRIC: { width: 110, height: 90, capacity: 2 },
  CARDIOLOGY: { width: 130, height: 110, capacity: 3 },
  NEUROLOGY: { width: 140, height: 120, capacity: 4 },
  ONCOLOGY: { width: 160, height: 130, capacity: 6 },
  ORTHOPEDICS: { width: 125, height: 105, capacity: 3 },
  RADIOLOGY: { width: 170, height: 135, capacity: 7 }
};
